import PropTypes from 'prop-types';
import React, {Component} from 'react';
import classNames from 'classnames';
import moment from 'moment';

import {chemicalBaseTypeNames, countries} from 'helpers/constants';
import {addCommas, systemOptionsTags} from 'helpers/dataHelpers';
import {join, replace} from 'helpers/functionalJsx';
import phoneFormat from 'helpers/phoneFormat';
import {isValidPhone, isValidEmail} from 'helpers/validation';

import {Radio, Checkbox, Input} from 'components/forms';
import AddressComplete from 'components/forms/LayoutAddressComplete';
import {formatCurrency} from '../../helpers/dashboardFormatter.jsx';

function addressValue(cust) {
  return [cust.address1, cust.address2, cust.city, cust.state, cust.zip].filter(x => !!x).join(', ');
}

import {FormattedMessage, intlShape, injectIntl} from 'react-intl';

class AccountCard extends Component {
  constructor(props) {
    super(props);

    this.contacts = [];
  }

  getCustomer = () => {
    let customer = this.props.customer;

    customer.accountNum = this.accountNum.getValue();
    let add = this.address.getValue();

    customer.address1 = add.address1;
    customer.address2 = add.address2;
    customer.city = add.city;
    customer.state = add.state;
    customer.zip = add.zip;
    customer.country = add.country;
    customer.addressCompleteID = add.addressCompleteID;
    customer.addressVerified = add.addressCompleteID ? 1 : 2;

    customer.customerContacts = this.contacts.filter(x => !!x).map(x => x.getContact());
    return customer;
  };

  registerContact = ref => {
    this.contacts.push(ref);
  };

  unregisterContact = ref => {
    this.contacts = this.contacts.filter(x => x !== ref && !!x);
  };

  render() {
    let {formatMessage, formatNumber} = this.props.intl;
    let {customer, active, title, dealer} = this.props;

    const classes = classNames('account-card', {
      'account-card--active': active,
    });

    let flags = <div className='pull-right'>
      {customer.flag ? <i className={classNames('pointer', 'icon', 'bl', 'bl-flag-filled', {
        'white': active,
      })}
                          data-event-category='User'
                          title={formatMessage({id: 'general.important.notes.flagged'})}
                          data-event-action='customer-flag'/> : null}
      {customer.favourite ? <i title={formatMessage({id: 'general.preferred.customer'})}
                               data-event-category='User'
                               data-event-action='customer-fav'
                               className={'pointer icon bl ' + (customer.favourite ? 'bl-star-filled' : 'bl-star')}/> : null}
      {dealer.isTruBlueEligible && (customer.isTruBlueInactive || customer.isTruBlueActive) ?
        <i className={classNames('bl', 'icon', 'pointer', {
          'white': active,
          'blue': !active,
          'bl-tru-blue-filled': customer.isTruBlueActive,
          'bl-tru-blue-empty': !customer.isTruBlueActive && customer.isTruBlueInactive,
        })}/> : null}
    </div>;

    return <div className={classes}>
      <div className='account-card__header'>
        {title}
        {flags}
      </div>

      <div className='account-card__details'>
        <h4 className='account-card__heading'><FormattedMessage id="general.account"/></h4>
        <div>
          {!active ? <Radio onChange={() => this.props.select('accountNum')}
                            checked={this.props.selected.includes('accountNum')}/> : null}
          <Input label={formatMessage({id: 'general.account.number'}) + ':'}
                 required
                 value={customer.accountNum}
                 editMode={!!active}
                 dataChanged={() => this.props.onDirty('accountNum')}
                 dirtied={customer.dirtied.accountNum}
                 group={this.props.group}
                 ref={an => this.accountNum = an}/>
        </div>
        <div>
          {!active ? <Radio onChange={() => this.props.select('address')}
                            checked={this.props.selected.includes('address')}/> : null}
          <AddressComplete label={formatMessage({id: 'general.address'}) + ':'}
                           required
                           dealer={dealer}
                           value={addressValue(customer)}
                           editMode={!!active}
                           dataChanged={() => this.props.onDirty('address')}
                           verified={customer.addressVerified === 1 || !!customer.addressCompleteID}
                           dirtied={customer.dirtied.address}
                           group={this.props.group}
                           style={!active ? {maxWidth: '80%'} : undefined}
                           ref={a => this.address = a}/>
        </div>
      </div>

      <div className='account-card__contacts'>
        <h4 className='account-card__heading'><FormattedMessage id="general.contacts"/></h4>
        {customer.customerContacts.map(x => <ContactSummary key={x.id}
                                                            contact={x}
                                                            active={!!active}
                                                            group={this.props.group}
                                                            unregisterContact={this.unregisterContact}
                                                            registerContact={this.registerContact}
                                                            selected={title === 'Account A'}
                                                            add={this.props.addContact}
                                                            remove={this.props.removeContact}
                                                            dealer={this.props.dealer}
                                                            formatMessage={formatMessage}/>)}
      </div>

      <div className='account-card__systems'>
        <h4 className='account-card__heading'><FormattedMessage id="general.systems"/></h4>
        {(customer.pools || []).map(x => <SystemSummary system={x}
                                                        key={x.id}
                                                        formatMessage={formatMessage}
                                                        dealer={dealer}/>)}
      </div>

      {dealer.CurrentPOSIntegration ?
        <React.Fragment>
          <div className='account-card__purchases'>
            <h4 className='account-card__heading'><FormattedMessage id="general.finalcial.metrics"/></h4>
            <div>
              <p><FormattedMessage id="general.last.purchase.amount"/></p>
              {customer.purchaseData ?
                <p>
                  <span>{customer.purchaseData ? formatCurrency(customer.purchaseData.LastPurchaseAmount) : ''}</span>
                  <span> | </span>
                  <span>{customer.purchaseData && customer.purchaseData.LastPurchaseDate.isValid() ? customer.purchaseData.LastPurchaseDate.format('ll') : ''}</span>
                </p> :
                <p><FormattedMessage id="general.none"/></p>}
            </div>
            <div>
              <p><FormattedMessage id="account.current.purchase.amount"/></p>
              <p>
                <span>{customer.purchaseData ? formatCurrency(customer.purchaseData.YearToDatePurchaseAmount) : ''}</span>
              </p>
            </div>
            <div>
              <p><FormattedMessage id="account.lifetime.value.customer"/></p>
              <p>
                <span>{formatCurrency(customer.POSTotalSales)}</span>
              </p>
            </div>
          </div>
          <div className='account-card__pos'>
            <h4 className='account-card__heading'>POS</h4>
            <div>
              <div>
                {!active ? <Radio onChange={() => this.props.select('posID')}
                                  checked={this.props.selected.includes('posID')}/> : null}
                <Input label='POS ID'
                       value={customer.POSVendorID}
                       editMode={!!active}
                       dataChanged={() => this.props.onDirty('posID')}
                       dirtied={customer.dirtied.posID}
                       group={this.props.group}
                       ref={id => this.POSVendorID = id}/>
              </div>
            </div>
          </div>
        </React.Fragment> : null}
    </div>;
  }
}

AccountCard.propTypes = {
  active: PropTypes.bool,
  customer: PropTypes.object.isRequired,
  dealer: PropTypes.object,
  title: PropTypes.string.isRequired,
  select: PropTypes.func,
  selected: PropTypes.array,
  addContact: PropTypes.func,
  removeContact: PropTypes.func,
  onDirty: PropTypes.func,
  group: PropTypes.object,
  intl: intlShape.isRequired,
};

class ContactSummary extends Component {
  constructor(props) {
    super(props);

    this.state = {
      contact: props.contact,
    };
  }

  componentWillMount() {
    this.props.registerContact(this);
  }

  componentWillUnmount() {
    this.props.unregisterContact(this);
  }

  getContact = () => {
    let contact = this.props.contact;

    contact.nameFirst = this.nameFirst.getValue();
    contact.nameLast = this.nameLast.getValue();
    contact.organization = this.organization.getValue();
    contact.homePhone = this.homePhone.getValue();
    contact.email = this.email.getValue();

    return contact;
  };

  dataChanged = e => {
    let contact = this.state.contact.clone();

    contact[e.key] = e.value;
    this.setState({contact});
  };

  render() {
    let {active, selected, add, remove, dealer, group, formatMessage} = this.props;
    let {contact} = this.state;
    const contactName = [contact.nameFirst, contact.nameLast].filter(x => !!x).join(' ');

    if (active) {
      return <div className='contact-summary contact-summary--active'>
        <Input noLabel
               required={!contact.organization || !contact.organization.trim()}
               placeholder={formatMessage({id: 'general.first.name'})}
               value={contact.nameFirst}
               group={group}
               ref={nf => this.nameFirst = nf}
               dataKey='nameFirst'
               dataChanged={this.dataChanged}/>
        <Input noLabel
               required={!contact.organization || !contact.organization.trim()}
               placeholder={formatMessage({id: 'general.last.name'})}
               value={contact.nameLast}
               group={group}
               ref={nl => this.nameLast = nl}
               dataKey='nameLast'
               dataChanged={this.dataChanged}/>
        <Input noLabel
               placeholder={formatMessage({id: 'customer.organization'})}
               value={contact.organization}
               group={group}
               ref={org => this.organization = org}
               dataKey='organization'
               dataChanged={this.dataChanged}/>
        <Input noLabel
               required
               placeholder={formatMessage({id: 'general.phone'})}
               format={phoneFormat}
               value={contact.homePhone || contact.mobilePhone || contact.workPhone}
               group={group}
               validate={isValidPhone}
               validateMessage={<FormattedMessage id='general.phone.validation'/>}
               ref={hp => this.homePhone = hp}
               dataKey='homePhone'
               dataChanged={this.dataChanged}/>
        <Input noLabel
               placeholder={formatMessage({id: 'general.email'})}
               value={contact.email}
               group={group}
               validate={isValidEmail}
               ref={e => this.email = e}
               validateMessage={<FormattedMessage id='general.email.validation'/>}
               dataKey='email'
               dataChanged={this.dataChanged}/>
      </div>;
    }

    const onChange = ({value}) => {
      if (value) {
        add(contact);
      } else {
        remove(contact);
      }
    };

    return <div className='contact-summary'>
      <Checkbox inline
                value={selected}
                dataChanged={onChange}/>
      <div className='contact-summary__content'>
        <p className='contact-summary__name'>{contactName}</p>
        {!contactName || !contactName.trim() ?
          <p className='contact-summary__organization'>{contact.organization}</p> :
          null
        }
        <p className='contact-summary__details'>
          P: {contact.homePhone || contact.workPhone || contact.mobilePhone || '--'}
          <br/>
          E: {contact.email || '--'}
        </p>
      </div>
    </div>;
  }
}

ContactSummary.propTypes = {
  contact: PropTypes.object.isRequired,
  active: PropTypes.bool,
  dealer: PropTypes.object,
  selected: PropTypes.bool,
  add: PropTypes.func,
  remove: PropTypes.func,
  group: PropTypes.object,
  registerContact: PropTypes.func,
  unregisterContact: PropTypes.func,
  formatMessage: PropTypes.any,
};


function SystemSummary({system, formatMessage, dealer}) {
  const options = systemOptionsTags[system.typeID];
  const finishTag = options.finish && options.finish[system.finish];
  const locationTag = options.location && options.location[system.location];

  const finish = finishTag ? replace(formatMessage({id: finishTag}), ' ',
    <span>&nbsp;</span>) : undefined;
  const location = locationTag ? formatMessage({id: locationTag}) : undefined;

  let systemProps = [
    location,
    system.volume ? addCommas(system.volume) + (dealer.country === countries.CA ? 'L' : ' gals.') : undefined,
    finish,
  ].filter(x => !!x);

  return <div className='system-summary'>
    <p
      className='system-summary__header'>{chemicalBaseTypeNames(system.typeID, system.sanitizerType, formatMessage)}</p>
    <p>
      {join(systemProps, ' | ')}
      {systemProps.length > 0 ? <br/> : null}
      <FormattedMessage
        id="general.last.analysis.date"/>: {system.prevAnalysisDate ? moment(system.prevAnalysisDate).format('ll') : formatMessage({id: 'general.never'})}
    </p>
  </div>;
}

SystemSummary.propTypes = {
  dealer: PropTypes.object.isRequired,
  system: PropTypes.object.isRequired,
  formatMessage: PropTypes.any,
};

export default injectIntl(AccountCard, {withRef: true});
